import {
  Alert,
  Button,
  Checkbox,
  FloatingIndicator,
  Input,
  List,
  NumberInput,
  SegmentedControl,
  Select,
  Tabs,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconCurrencyEuro, IconPercentage, IconPlus } from '@tabler/icons-react';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { EventRecurrenceCreationForm } from 'src/Components/Forms/Event/EventForm/EventRecurrenceCreationForm';
import { LocativeShareInput } from 'src/Components/Forms/Expense/LocativeShareInput';
import { CategoryPicker } from 'src/Components/Inputs/CategoryPicker';
import { UploadFileButton } from 'src/Components/Inputs/UploadFileButton';
import { EXPENSE_TYPE_TREE, MainCategory } from 'src/Constants/expenseCategorization';
import { CreateExpenseFormSplitScope } from 'src/Hooks/Forms/useCreateExpenseForm';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchBankAccounts from 'src/Hooks/Queries/useFetchBankAccounts';
import useFetchContacts from 'src/Hooks/Queries/useFetchContacts';
import useFetchHouseholds from 'src/Hooks/Queries/useFetchHouseholds';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { DEFAULT_UPLOAD_FOLDER, useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';
import {
  AssetDetailsDtoRentalDeclarationRegimeEnum,
  CreateExpenseDtoSplitTypeEnum,
} from 'src/Services/API/generated';
import { trackReceiptAdded } from 'src/Services/Mixpanel/events';

const CREATE_CONTACT_SENTINEL = '__create_contact__';

import FormAttachmentViewer from '../../FormAttachmentViewer/FormAttachmentViewer.component';

import styles from './ExpenseForm.module.scss';
import { Props } from './ExpenseForm.types';

const ExpenseForm = ({
  form,
  onSubmit,
  onCancel,
  submitButtonLabel,
  isEdition,
  extraPayerOptions,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: membersData } = useFetchMembers();
  const { data: bankAccounts } = useFetchBankAccounts();
  const { data: contacts } = useFetchContacts();
  const { data: user } = useFetchUser();
  const { data: asset } = useFetchAssetDetails();
  const { data: households } = useFetchHouseholds();

  const [categoryPickerOpened, setCategoryPickerOpened] = useState(false);

  const categoryPath = useMemo(() => {
    const type = form.values.type;
    if (!type) return '';
    const meta = EXPENSE_TYPE_TREE[type];
    if (!meta) return t(`expense.type.${type}`, type);
    return `${t(`expense.mainCategory.${meta.main}`)} › ${t(`expense.subCategory.${meta.sub}`)} › ${t(`expense.type.${type}`)}`;
  }, [form.values.type, t]);

  const isFundCall = useMemo(
    () =>
      !!form.values.type && EXPENSE_TYPE_TREE[form.values.type]?.main === MainCategory.FundingCalls,
    [form.values.type]
  );

  const splitTypeLabels = useMemo(
    () => ({
      [CreateExpenseDtoSplitTypeEnum.Equal]: t('expense.splitType.fairly', 'Équitablement'),
      [CreateExpenseDtoSplitTypeEnum.Amount]: t('expense.splitType.amounts', 'En montants'),
      [CreateExpenseDtoSplitTypeEnum.Percentage]: t('expense.splitType.parts', 'En parts'),
    }),
    [t]
  );

  const suggestedSplitType = CreateExpenseDtoSplitTypeEnum.Equal;
  const splitDivergesFromSuggestion =
    form.values.splitType !== suggestedSplitType || form.values.shares.some(s => !s.selected);
  const [splitManuallyExpanded, setSplitManuallyExpanded] = useState(false);
  const splitExpanded = splitManuallyExpanded || splitDivergesFromSuggestion;

  const handleCategoryChange = useCallback(
    (newType: typeof form.values.type) => {
      form.setFieldValue('type', newType);
      form.setFieldValue('splitType', suggestedSplitType);
      form.setFieldValue(
        'shares',
        form.values.shares.map(s => ({ ...s, selected: true, amount: 0, percentage: 0 }))
      );
      setSplitManuallyExpanded(false);
    },
    [form, suggestedSplitType]
  );

  const payerOptions = useMemo(() => {
    // member.user.id is actually a PlaceholderMember id for unregistered co-owners
    // (isPlaceholder), hence the distinct prefix so it's routed to paidByPlaceholderMemberId.
    const memberOptions = (membersData?.members ?? []).map(member => ({
      label: `${member.user.firstName} ${member.user.lastName}`,
      value: member.isPlaceholder ? `placeholder-${member.user.id}` : `user-${member.user.id}`,
    }));

    const bankAccountOptions = (bankAccounts ?? []).map(account => ({
      label: account.name,
      value: `bank-${account.id}`,
    }));

    // A1 — a Contact homonymous with a co-owner is how the ECO COMPTEUR expense was
    // charged to a third party by mistake. The member always comes first (group order),
    // and the homonymous contact is labelled and disabled so it cannot be picked by slip.
    const memberNames = new Set(
      (membersData?.members ?? []).map(member =>
        `${member.user.firstName ?? ''} ${member.user.lastName ?? ''}`.trim().toLowerCase()
      )
    );

    const contactOptions = (contacts ?? []).map(contact => {
      const name = contact.displayName;
      const isHomonym = memberNames.has(name.trim().toLowerCase());
      return {
        label: isHomonym
          ? t('expense.form.contactHomonym', '{{name}} (contact, pas le copropriétaire)', { name })
          : t('expense.form.contactOption', '{{name}} (contact)', { name }),
        value: `contact-${contact.id}`,
        disabled: isHomonym,
      };
    });

    // In edition, a placeholder payer may have been removed from the members list since the
    // expense was recorded; extraPayerOptions carries it so the field can render its label.
    const knownValues = new Set([
      ...memberOptions.map(o => o.value),
      ...bankAccountOptions.map(o => o.value),
      ...(contacts ?? []).map(c => `contact-${c.id}`),
    ]);
    const extraMemberOptions = (extraPayerOptions ?? []).filter(
      option => !knownValues.has(option.value)
    );
    const enrichedMemberOptions = [...memberOptions, ...extraMemberOptions];

    const options = [];
    if (enrichedMemberOptions.length > 0) {
      options.push({
        group: t('expense.form.members', 'Membres'),
        items: enrichedMemberOptions,
      });
    }
    if (bankAccountOptions.length > 0) {
      options.push({
        group: t('expense.form.bankAccounts', 'Comptes bancaires'),
        items: bankAccountOptions,
      });
    }
    if (!form.values.isIncome) {
      options.push({
        group: t('expense.form.contacts', 'Contacts'),
        items: [
          ...contactOptions,
          {
            label: t('expense.form.createContact', 'Créer un contact'),
            value: CREATE_CONTACT_SENTINEL,
          },
        ],
      });
    }
    return options;
  }, [membersData, bankAccounts, contacts, form.values.isIncome, extraPayerOptions, t]);

  // A fund call payer must be a real member account: shares only accept a User id, not a
  // PlaceholderMember one (unregistered co-owners are excluded here for that reason).
  const fundCallPayerOptions = useMemo(
    () =>
      (membersData?.members ?? [])
        .filter(member => !member.isPlaceholder)
        .map(member => ({
          label: `${member.user.firstName} ${member.user.lastName}`,
          value: member.user.id,
        })),
    [membersData]
  );

  const fundCallPayer = useMemo(
    () => membersData?.members.find(member => member.user.id === form.values.fundCallPayerId),
    [membersData, form.values.fundCallPayerId]
  );

  const fundCallNetsToZero =
    !!form.values.fundCallPayerId && form.values.paidBy === `user-${form.values.fundCallPayerId}`;

  const handlePayerChange = useCallback(
    (value: string | null) => {
      if (value === CREATE_CONTACT_SENTINEL) {
        if (asset?.id) {
          navigate(`/asset/${asset.id}/contacts/new`);
        }
        return;
      }
      form.setFieldValue('paidBy', value ?? undefined);
    },
    [form, navigate, asset?.id]
  );

  const reimbursementOptions = useMemo(() => {
    const options = [
      {
        label: t('expense.form.reimbursePayer', 'Rembourser le payeur (Défaut)'),
        value: '',
      },
    ];

    const bankAccountOptions = (bankAccounts ?? []).map(account => ({
      label: account.name,
      value: `bank-${account.id}`,
    }));

    if (bankAccountOptions.length > 0) {
      options.push(...bankAccountOptions);
    }
    return options;
  }, [bankAccounts, t]);

  const showLocativeBlock =
    !form.values.isIncome &&
    asset?.rentalDeclarationRegime === AssetDetailsDtoRentalDeclarationRegimeEnum.Reel &&
    !asset?.hasDismemberment;

  useEffect(() => {
    if (
      showLocativeBlock &&
      form.values.locativeShare === undefined &&
      asset?.defaultLocativeShare != null
    ) {
      form.setFieldValue('locativeShare', asset.defaultLocativeShare);
    }
  }, [showLocativeBlock, asset?.defaultLocativeShare]);

  useEffect(() => {
    const currentType = form.values.type;
    if (
      currentType &&
      EXPENSE_TYPE_TREE[currentType] &&
      EXPENSE_TYPE_TREE[currentType].isIncome !== form.values.isIncome
    ) {
      // The previously picked category is not valid in the new tab: clear it so the user
      // makes an explicit choice — we no longer guess a "first valid" default.
      form.setFieldValue('type', undefined);
    }
  }, [form.values.isIncome]);

  const shareError = form.errors['shares'];
  const householdShareError = form.errors['householdShares'];
  const isByHousehold = form.values.splitScope === CreateExpenseFormSplitScope.HOUSEHOLD;
  const hasHouseholds = (households?.length ?? 0) > 0;

  // Initialize the householdShares list from the loaded households the first time.
  useEffect(() => {
    if (!households || households.length === 0) return;
    if (form.values.householdShares.length > 0) return;
    form.setFieldValue(
      'householdShares',
      households.map(h => ({
        householdId: h.id,
        selected: true,
        amount: 0,
        percentage: 0,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [households]);

  useEffect(() => {
    if (form.values.isIncome && form.values.paidBy?.startsWith('contact-')) {
      form.setFieldValue('paidBy', user ? `user-${user.id}` : undefined);
      notifications.show({
        color: 'yellow',
        message: t(
          'expense.form.contactPayerResetOnIncome',
          'Un contact ne peut pas encaisser une recette : payeur remis à vous-même.'
        ),
      });
    }
  }, [form.values.isIncome, form.values.paidBy, user, t]);

  // Fund calls are received on the asset's bank account by default, not by a person.
  useEffect(() => {
    if (!isFundCall || form.values.paidBy?.startsWith('bank-')) return;
    const firstBankAccount = bankAccounts?.[0];
    if (!firstBankAccount) return;
    form.setFieldValue('paidBy', `bank-${firstBankAccount.id}`);
  }, [isFundCall, bankAccounts, form.values.paidBy]);

  // In fund call mode the whole amount is credited to a single member (100% share).
  useEffect(() => {
    if (!isFundCall || !form.values.fundCallPayerId) return;
    form.setFieldValue('splitType', CreateExpenseDtoSplitTypeEnum.Equal);
    form.setFieldValue('splitScope', CreateExpenseFormSplitScope.INDIVIDUAL);
    form.setFieldValue(
      'shares',
      form.values.shares.map(share => ({
        ...share,
        selected: share.participantId === form.values.fundCallPayerId,
        amount: 0,
        percentage: 0,
      }))
    );
  }, [isFundCall, form.values.fundCallPayerId, form.values.shares.length]);

  const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
  const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLButtonElement | null>>({});
  const setControlRef = (val: string) => (node: HTMLButtonElement) => {
    controlsRefs[val] = node;
    setControlsRefs(controlsRefs);
  };

  const value = useMemo(
    () => (form.values.isIncome ? 'income' : 'expense'),
    [form.values.isIncome]
  );

  const { uploads, startUpload, removeUpload, isLoading } = useFileUploadManager({
    onUploadReady: readyFile => {
      if (!readyFile.fileData) {
        console.error('readyFile.fileData should not be undefined.');
        return;
      }

      const attachment = {
        video: readyFile.videoData,
        // TODO : if id is not defined here for video, try readyFile.videoData?.fileId
        fileId: readyFile.fileData.id,
        type: readyFile.fileData.type,
        name: readyFile.fileData.name,
        previewUrl: readyFile.fileData!.urls?.previewThumbnailUrl,
      };

      form.setFieldValue('attachments', current => [...current, attachment]);

      const ocrData = readyFile.ocrData;
      const hasOcrData = ocrData && (ocrData.title || ocrData.amount || ocrData.date);

      if (hasOcrData) {
        const ocrDetails = [];
        if (ocrData.title) {
          ocrDetails.push(
            <List.Item key="title">
              <div className={styles.ocrLine}>
                <div>{t('expenses.form.ocr.title', 'Titre : ')}</div>
                <div className={styles.ocrItem}>{ocrData.title}</div>
              </div>
            </List.Item>
          );
        }
        if (ocrData?.merchantName) {
          ocrDetails.push(
            <List.Item key="title">
              <div className={styles.ocrLine}>
                <div>{t('expenses.form.ocr.merchant', 'Enseigne : ')}</div>
                <div className={styles.ocrItem}>{ocrData.merchantName}</div>
              </div>
            </List.Item>
          );
        }
        if (ocrData.amount) {
          ocrDetails.push(
            <List.Item key="amount">
              <div className={styles.ocrLine}>
                <div> {t('expenses.form.ocr.amount', 'Montant : ')}</div>
                <div className={styles.ocrItem}>{ocrData.amount.toFixed(2)} €</div>
              </div>
            </List.Item>
          );
        }
        if (ocrData.date) {
          ocrDetails.push(
            <List.Item key="date">
              <div className={styles.ocrLine}>
                <div>{t('expenses.form.ocr.date', 'Date : ')}</div>
                <div className={styles.ocrItem}>{ocrData.date.format('LL')}</div>
              </div>
            </List.Item>
          );
        }

        if (ocrData?.type) {
          ocrDetails.push(
            <List.Item key="date">
              <div className={styles.ocrLine}>
                <div>{t('expenses.form.ocr.category', 'Catégorie : ')}</div>
                <div className={styles.ocrItem}>{t(`expense.type.${ocrData.type}`)}</div>
              </div>
            </List.Item>
          );
        }

        modals.openContextModal({
          modal: 'info',
          title: t('expense.form.ocrModal.title', 'Données extraites du justificatif'),
          centered: true,
          size: 'md',
          innerProps: {
            content: (
              <div>
                <div className={styles.ocrParagraph}>
                  {t(
                    'expense.form.ocrModal.content',
                    'Nous avons analysé votre document et trouvé les informations suivantes. Souhaitez-vous les appliquer au formulaire ?'
                  )}
                </div>
                <List spacing="xs" size="sm" center>
                  {ocrDetails}
                </List>
              </div>
            ),
            confirmText: t('expense.form.ocrModal.confirmButton', 'Oui, appliquer'),
            cancelText: t('expense.form.ocrModal.cancelButton', 'Non, merci'),
            onConfirm: () => {
              if (ocrData.title) form.setFieldValue('title', ocrData.title);
              if (ocrData?.merchantName) form.setFieldValue('merchant', ocrData.merchantName);
              if (ocrData.amount) form.setFieldValue('amount', ocrData.amount);
              if (ocrData.date) form.setFieldValue('paidAt', ocrData.date.toDate());
              if (ocrData?.type) form.setFieldValue('type', ocrData.type);
            },
            onCancel: () => {},
          },
        });
      }
    },
  });

  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();

  const handleExistingFilesSelect = async (fileIds: string[]) => {
    const newAttachments = await Promise.all(
      fileIds.map(async fileId => {
        const fileInfo = await fetchFileInfo(fileId);
        return {
          video: fileInfo.video,
          fileId: fileInfo.id,
          type: fileInfo.type,
          name: fileInfo.name,
          previewUrl: fileInfo.urls?.previewThumbnailUrl,
        };
      })
    );
    form.setFieldValue('attachments', current => [...current, ...newAttachments]);
  };
  return (
    <form id="checklist-form" onSubmit={onSubmit} className={styles.container}>
      <Tabs
        value={value}
        className={styles.tabs}
        onChange={value => form.setFieldValue('isIncome', value === 'income')}
      >
        <Tabs.List ref={setRootRef} className={styles.tabList}>
          <Tabs.Tab value={'expense'} ref={setControlRef('expense')}>
            {t('expenses.form.expense', 'Dépense')}
          </Tabs.Tab>
          <Tabs.Tab value={'income'} ref={setControlRef('income')}>
            {t('expenses.form.income', 'Recette')}
          </Tabs.Tab>

          <FloatingIndicator
            target={value ? controlsRefs[value] : null}
            parent={rootRef}
            className={styles.indicator}
          />
        </Tabs.List>
      </Tabs>
      <TextInput
        label={form.values.title && t('expense.form.title', 'Titre')}
        placeholder={
          form.values.isIncome
            ? t(
                'expense.form.incomeTitlePlaceholder',
                'Titre du revenu (exemple : Loyer du mois de mai)'
              )
            : t(
                'expense.form.titlePlaceholder',
                'Titre de la dépense (exemple : Colonne de douche)'
              )
        }
        {...form.getInputProps('title')}
      />
      <UploadFileButton
        label={t('expense.form.addReceipt', 'Ajouter un justificatif')}
        modalTitle={t('expense.form.addReceiptModalTitle', 'Ajouter un justificatif')}
        cameraSubtitle={t(
          'expense.form.cameraSubtitle',
          'Lecture auto du montant, date et fournisseur'
        )}
        fileUploadSubtitle={t('expense.form.fileUploadSubtitle', 'PDF, image ou document')}
        existingFileLabel={t('expense.form.existingFileLabel', 'Lier un justificatif existant')}
        existingFileSubtitle={t(
          'expense.form.existingFileSubtitle',
          'Depuis vos documents déjà ajoutés'
        )}
        onSourceSelected={source =>
          trackReceiptAdded({
            source,
            is_income: form.values.isIncome,
            asset_id: asset?.id,
          })
        }
        onFilesSelected={files => startUpload(files, DEFAULT_UPLOAD_FOLDER, 'EXPENSE_RECEIPT')}
        onExistingFilesSelect={handleExistingFilesSelect}
        loading={isLoading}
      />
      <TextInput
        label={form.values.merchant && t('expense.form.merchant', 'Enseigne/Fournisseur')}
        placeholder={t(
          'expense.form.merchantPlaceholder',
          'Enseigne (exemple: Leroy Merlin, Ikea, EDF...)'
        )}
        {...form.getInputProps('merchant')}
      />
      <Textarea
        label={form.values.note && t('expense.form.note', "Détails de l'opération")}
        placeholder={t(
          'expense.form.notePlaceholder',
          "Détails de l'opération (exemple : Remplacement serrure portail + 2 clés)"
        )}
        autosize
        minRows={2}
        maxRows={5}
        {...form.getInputProps('note')}
      />
      <FormAttachmentViewer
        attachments={form.values.attachments}
        uploadingAttachments={uploads}
        onDeleteAttachment={attachment => {
          form.setFieldValue(
            'attachments',
            form.values.attachments.filter(a => a.fileId !== attachment.fileId)
          );
        }}
        onDeleteUploadingAttachment={localId => removeUpload(localId)}
      />
      <NumberInput
        label={t('expense.form.amount', 'Montant')}
        placeholder={t('expense.form.amount', 'Montant')}
        {...form.getInputProps('amount')}
        rightSection={<IconCurrencyEuro size={20} style={{ marginRight: '10px' }} />}
      />
      {isFundCall && (
        <Select
          style={{ width: '100%' }}
          label={t('expense.form.fundCallPaidBy', 'Payé par')}
          data={fundCallPayerOptions}
          searchable
          value={form.values.fundCallPayerId ?? null}
          onChange={value => form.setFieldValue('fundCallPayerId', value ?? undefined)}
          error={form.errors['fundCallPayerId']}
        />
      )}
      {isEdition && form.values.paidBy === undefined && (
        <Alert color="yellow" variant="light">
          {t(
            'expense.form.legacyMissingPayer',
            "Cette opération n'a pas de payeur enregistré. Choisissez-le pour pouvoir enregistrer."
          )}
        </Alert>
      )}
      <div className={styles.formLine}>
        {/* Same shape as the Catégorie field: the wrapper owns the label and the message, so the
            error reads under the field instead of inside it. The Select only gets a boolean, which
            paints the border red without repeating the text in the control. */}
        <Input.Wrapper
          style={{ width: '100%' }}
          label={
            form.values.isIncome
              ? t('expense.form.receivedBy', 'Encaissé par')
              : t('expense.form.paidBy', 'Payé par')
          }
          error={form.errors['paidBy']}
        >
          <Select
            placeholder={
              form.values.isIncome
                ? t('expense.form.receivedByPlaceholder', 'Choisir qui a encaissé')
                : t('expense.form.paidByPlaceholder', 'Choisir qui a payé')
            }
            data={payerOptions}
            searchable
            allowDeselect={false}
            value={form.values.paidBy ?? null}
            onChange={handlePayerChange}
            error={!!form.errors['paidBy']}
            classNames={{ input: styles.payerInput }}
            renderOption={({ option }) =>
              option.value === CREATE_CONTACT_SENTINEL ? (
                <div className={styles.createContactOption}>
                  <IconPlus size={16} />
                  <span>{option.label}</span>
                </div>
              ) : (
                <span>{option.label}</span>
              )
            }
          />
        </Input.Wrapper>
        <DateTimePicker
          style={{ width: '100%' }}
          label={t('expense.form.paidDate', 'Date de paiement')}
          {...form.getInputProps('paidAt')}
        />
      </div>
      {!form.values.isIncome && (
        <Select
          style={{ width: '100%' }}
          label={t('expense.form.reimburseTo', 'Rembourser via')}
          data={reimbursementOptions}
          allowDeselect={false}
          {...form.getInputProps('reimburseToBankAccount')}
        />
      )}
      <Input.Wrapper label={t('expense.form.category', 'Catégorie')} error={form.errors['type']}>
        <Input
          component="button"
          type="button"
          pointer
          onClick={() => setCategoryPickerOpened(true)}
          rightSection={<IconChevronDown size={16} />}
          classNames={{ input: styles.categoryButton }}
        >
          <span className={styles.categoryButtonLabel}>
            {categoryPath || t('expense.form.categoryPlaceholder', 'Sélectionner une catégorie')}
          </span>
        </Input>
      </Input.Wrapper>
      <CategoryPicker
        opened={categoryPickerOpened}
        onClose={() => setCategoryPickerOpened(false)}
        isIncome={form.values.isIncome}
        value={form.values.type}
        onChange={handleCategoryChange}
      />
      {showLocativeBlock && (
        <LocativeShareInput
          value={form.values.locativeShare}
          onChange={value => form.setFieldValue('locativeShare', value)}
          totalAmount={form.values.amount}
          defaultValue={asset?.defaultLocativeShare ?? undefined}
        />
      )}
      {isFundCall && (
        <Alert color="green" variant="light">
          {fundCallPayer
            ? t(
                'expense.form.fundCallCreditBanner',
                'Crédité à 100 % à {{firstName}} {{lastName}}. Son solde augmente de {{amount}} €.',
                {
                  firstName: fundCallPayer.user.firstName,
                  lastName: fundCallPayer.user.lastName,
                  amount: (form.values.amount ?? 0).toFixed(2),
                }
              )
            : t(
                'expense.form.fundCallSelectPayerHint',
                "Sélectionnez qui a payé pour voir l'effet sur son solde."
              )}
          {fundCallNetsToZero && (
            <div>
              {t(
                'expense.form.fundCallNetsToZero',
                "Le compte encaissant est la même personne que le payeur : l'effet net sur son solde est nul."
              )}
            </div>
          )}
        </Alert>
      )}
      {!isFundCall && (
        <>
          {hasHouseholds && (
            <div className={styles.splitScopeRow}>
              <SegmentedControl
                fullWidth
                value={form.values.splitScope}
                onChange={value =>
                  form.setFieldValue('splitScope', value as CreateExpenseFormSplitScope)
                }
                data={[
                  {
                    value: CreateExpenseFormSplitScope.INDIVIDUAL,
                    label: t('expense.form.splitScopeIndividual', 'Par personne'),
                  },
                  {
                    value: CreateExpenseFormSplitScope.HOUSEHOLD,
                    label: t('expense.form.splitScopeHousehold', 'Par foyer'),
                  },
                ]}
              />
            </div>
          )}
          {!splitExpanded && !isByHousehold && (
            <button
              type="button"
              className={styles.splitSuggestionLabel}
              onClick={() => setSplitManuallyExpanded(true)}
            >
              {t('expense.form.suggestedSplit', 'Répartition suggérée')} :{' '}
              <span className={styles.splitSuggestionValue}>
                {splitTypeLabels[suggestedSplitType]}
              </span>
            </button>
          )}
          <div
            className={classnames(styles.shareContainer, {
              [styles.shareContainerHidden]: !splitExpanded && !isByHousehold,
            })}
          >
            <div className={styles.topLine}>
              <div className={styles.sharedWith}>
                {isByHousehold
                  ? t('expense.form.shareWithHouseholds', 'Partager entre foyers')
                  : t('expense.form.shareWith', 'Partager avec')}
              </div>
              <Select
                variant={'unstyled'}
                data={[
                  {
                    label: t('expense.splitType.fairly', 'Équitablement'),
                    value: CreateExpenseDtoSplitTypeEnum.Equal,
                  },
                  {
                    label: t('expense.splitType.amounts', 'En montants'),
                    value: CreateExpenseDtoSplitTypeEnum.Amount,
                  },
                  {
                    label: t('expense.splitType.parts', 'En parts'),
                    value: CreateExpenseDtoSplitTypeEnum.Percentage,
                  },
                ]}
                {...form.getInputProps('splitType')}
              />
            </div>
            {isByHousehold && (
              <div className={styles.members}>
                {form.values.householdShares.map((share, index) => {
                  const household = (households ?? []).find(h => h.id === share.householdId);
                  if (!household) return null;
                  const selectedCount = form.values.householdShares.filter(s => s.selected).length;
                  return (
                    <div className={styles.memberLine} key={share.householdId}>
                      <Checkbox
                        label={household.name}
                        {...form.getInputProps(`householdShares.${index}.selected`, {
                          type: 'checkbox',
                        })}
                        onChange={event => {
                          form.getInputProps(`householdShares.${index}.selected`).onChange(event);
                          if (!event.target.checked) {
                            form.setFieldValue(`householdShares.${index}.amount`, 0);
                            form.setFieldValue(`householdShares.${index}.percentage`, 0);
                          }
                        }}
                      />
                      {form.values.splitType === CreateExpenseDtoSplitTypeEnum.Equal && (
                        <TextInput
                          wrapperProps={{ className: styles.memberAmountInput }}
                          height={38}
                          className={styles.memberAmountInput}
                          readOnly
                          value={
                            share.selected && selectedCount > 0
                              ? ((form.values.amount ?? 0) / selectedCount).toFixed(2)
                              : 0
                          }
                          rightSection={
                            <IconCurrencyEuro size={15} style={{ marginRight: '2px' }} />
                          }
                        />
                      )}
                      {form.values.splitType === CreateExpenseDtoSplitTypeEnum.Amount && (
                        <NumberInput
                          wrapperProps={{ className: styles.memberAmountInput }}
                          height={38}
                          className={styles.memberAmountInput}
                          rightSection={
                            <IconCurrencyEuro size={15} style={{ marginRight: '2px' }} />
                          }
                          {...form.getInputProps(`householdShares.${index}.amount`)}
                        />
                      )}
                      {form.values.splitType === CreateExpenseDtoSplitTypeEnum.Percentage && (
                        <NumberInput
                          wrapperProps={{ className: styles.memberAmountInput }}
                          height={38}
                          className={styles.memberPercentageInput}
                          rightSection={<IconPercentage size={15} style={{ marginRight: '2px' }} />}
                          {...form.getInputProps(`householdShares.${index}.percentage`)}
                        />
                      )}
                    </div>
                  );
                })}
                {householdShareError && <div className={styles.error}>{householdShareError}</div>}
              </div>
            )}
            {!isByHousehold && (
              <>
                <div className={styles.members}>
                  {form.values.shares.map((share, index) => {
                    const member = membersData?.members.find(
                      m => m.user.id === share.participantId
                    );
                    return (
                      <div className={styles.memberLine} key={share.participantId}>
                        <Checkbox
                          key={share.participantId}
                          label={[member?.user.firstName, member?.user.lastName].join(' ')}
                          {...form.getInputProps(`shares.${index}.selected`, { type: 'checkbox' })}
                          onChange={event => {
                            form.getInputProps(`shares.${index}.selected`).onChange(event);
                            if (!event.target.checked) {
                              form.setFieldValue(`shares.${index}.amount`, 0);
                              form.setFieldValue(`shares.${index}.percentage`, 0);
                            }
                          }}
                        />
                        {form.values.splitType === CreateExpenseDtoSplitTypeEnum.Equal && (
                          <TextInput
                            wrapperProps={{ className: styles.memberAmountInput }}
                            height={38}
                            className={styles.memberAmountInput}
                            readOnly
                            value={
                              form.values.shares[index].selected
                                ? (
                                    (form.values.amount ?? 0) /
                                    form.values.shares.filter(s => s.selected).length
                                  ).toFixed(2)
                                : 0
                            }
                            rightSection={
                              <IconCurrencyEuro size={15} style={{ marginRight: '2px' }} />
                            }
                          />
                        )}
                        {form.values.splitType === CreateExpenseDtoSplitTypeEnum.Amount && (
                          <NumberInput
                            wrapperProps={{ className: styles.memberAmountInput }}
                            height={38}
                            className={styles.memberAmountInput}
                            rightSection={
                              <IconCurrencyEuro size={15} style={{ marginRight: '2px' }} />
                            }
                            {...form.getInputProps(`shares.${index}.amount`)}
                          />
                        )}
                        {form.values.splitType === CreateExpenseDtoSplitTypeEnum.Percentage && (
                          <NumberInput
                            wrapperProps={{ className: styles.memberAmountInput }}
                            height={38}
                            className={styles.memberPercentageInput}
                            rightSection={
                              <IconPercentage size={15} style={{ marginRight: '2px' }} />
                            }
                            {...form.getInputProps(`shares.${index}.percentage`)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
                {shareError && <div className={styles.error}>{shareError}</div>}
              </>
            )}
          </div>
        </>
      )}
      {/*
        Recurrence is carried by the Expense entity itself and the engine never filters on
        isIncome, so incomes (rents, seasonal lettings) get the very same block as charges.
      */}
      <EventRecurrenceCreationForm
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form={form as any}
        paths={{
          frequency: 'recurrence.frequency',
          interval: 'recurrence.interval',
          customFrequency: 'recurrence.customFrequency',
          monthDayNumberOrAdvanced: 'recurrence.monthDayNumberOrAdvanced',
          weekDays: 'recurrence.byWeekDays',
          monthDayNumber: 'recurrence.byMonthDays',
          month: 'recurrence.byMonth',
          advancedMonthDay: 'recurrence.byWeekDay',
          advancedMonthWeek: 'recurrence.byMonthDay',
          monthDaySelected: 'recurrence.monthDaySelected',
          until: 'recurrence.until',
        }}
        translationOverrides={{
          none: t('expense.form.noRecurrence', 'Pas de récurrence'),
        }}
      />
      <div className={styles.buttonsContainer}>
        <Button onClick={onCancel} variant="outline" className="btn-medium">
          {t('expense.form.cancel', 'Annuler')}
        </Button>
        <Button type="submit" color={form.values.isIncome ? 'green' : 'orange'}>
          {submitButtonLabel}
        </Button>
      </div>
    </form>
  );
};

export default ExpenseForm;
